/**
 * Generative player marks (v29) — every bettor is a TARGET the Footfall
 * street camera has locked: the AI person-detection BOUNDING BRACKET around
 * a colored core (the B direction), with a slowly rotating DASHED
 * calibration ring between them carrying the scope/reticle flavor (the C
 * direction) — surveillance, not weaponry. The ring's spin speed and
 * direction are seeded per player, so a full feed drifts asynchronously:
 * alive, but never busy.
 *
 * Fully deterministic from the seed (name+country — the only identity the
 * social wire carries), so a returning player wears the same mark every
 * session. Pure math here (unit-tested); the SVG lives in PlayerAvatar.
 */

/** Stable seed for a feed entry. Anonymous players share one calm mark. */
export function avatarSeed(name: string | undefined, country: string | undefined): string {
  return `${name ?? '?'}·${country ?? ''}`
}

function hash32(seed: string): number {
  // FNV-1a — good nibble dispersion so the derived fields don't correlate
  // for near-identical seeds ("ZippyInferno·TH" vs "·US").
  let hash = 0x811c9dc5
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

export interface AvatarParams {
  /** Base hue of the locked core. */
  hue: number
  /** Companion hue — the calibration ring. A wide, seeded offset so the two
   * tones always read as a distinct pair. */
  hueB: number
  /** Core saturation, 62..82 (%). */
  sat: number
  /** Initial rotation of the calibration ring, degrees. */
  sheenAngle: number
  /** Bracket gap variant (0..2) — how open the detection corners sit. */
  gapVariant: number
  /** Calibration-ring spin period, 7..16 (s) — seeded so a feed of marks
   * drifts asynchronously. */
  spinSeconds: number
  /** Spin direction: 1 clockwise, -1 counter. */
  spinDirection: 1 | -1
}

export function avatarParams(seed: string): AvatarParams {
  const h = hash32(seed)
  return {
    hue: h % 360,
    hueB: (h % 360 + 90 + ((h >>> 9) % 150)) % 360,
    sat: 62 + ((h >>> 17) % 21),
    sheenAngle: (h >>> 3) % 360,
    gapVariant: (h >>> 5) % 3,
    spinSeconds: 7 + ((h >>> 11) % 10),
    spinDirection: ((h >>> 13) & 1) === 0 ? 1 : -1
  }
}
